import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRecordVitals } from "@/lib/mutations";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";
import type { AppointmentRow as ApptRow } from "@/lib/queries";

export function ReceptionVitalsDialog({ open, onOpenChange, appointment }: { open: boolean; onOpenChange: (v: boolean) => void; appointment: ApptRow | null }) {
  const [form, setForm] = useState({ tempC: "", bpSystolic: "", bpDiastolic: "", pulse: "", respRate: "", spO2: "", heightCm: "", weightKg: "" });
  const [visitId, setVisitId] = useState<string | null>(null);

  const record = useRecordVitals(visitId ?? "");

  const bmi = (() => {
    // Height is integer-only (API rejects decimals) — parse it the same way
    // the submit does so the displayed BMI matches what gets saved.
    const h = parseInt(form.heightCm, 10); const w = parseFloat(form.weightKg);
    if (!h || !w) return "";
    return (w / Math.pow(h / 100, 2)).toFixed(1);
  })();

  const findVisit = async (): Promise<string | null> => {
    try {
      const r = await apiFetch<{ visit?: { id: string }; rows?: { id: string }[] }>(`/visits?appointmentId=${appointment?.id ?? ""}`);
      const v = r.visit ?? r.rows?.[0];
      if (v) { setVisitId(v.id); return v.id; }
      return null;
    } catch { return null; }
  };

  const submit = async () => {
    if (!appointment) return;
    // All fields empty → nothing to record. Skip the POST entirely: a blank
    // submit would overwrite the visit's vitals row with all nulls (the API
    // is an UPSERT on visit_id since 2026-10-27, post-mortem #256 — this
    // dialog has no clear-existing semantics; it only records new measurements).
    const anyValue = Object.values(form).some((v) => v.trim() !== "");
    if (!anyValue) { toast.info("No vitals entered — nothing saved."); onOpenChange(false); return; }
    let vid = visitId;
    if (!vid) vid = await findVisit();
    if (!vid) { toast.error("No visit found — check in the patient first"); return; }
    setVisitId(vid);
    try {
      await record.mutateAsync({
        tempC: form.tempC ? parseFloat(form.tempC) : null,
        bpSystolic: form.bpSystolic ? parseInt(form.bpSystolic, 10) : null,
        bpDiastolic: form.bpDiastolic ? parseInt(form.bpDiastolic, 10) : null,
        pulse: form.pulse ? parseInt(form.pulse, 10) : null,
        respRate: form.respRate ? parseInt(form.respRate, 10) : null,
        // spO2/heightCm are integer-only (API rejects decimals).
        spO2: form.spO2 ? parseInt(form.spO2, 10) : null,
        heightCm: form.heightCm ? parseInt(form.heightCm, 10) : null,
        weightKg: form.weightKg ? parseFloat(form.weightKg) : null,
        bmi: bmi ? parseFloat(bmi) : null,
      });
      onOpenChange(false);
    } catch { /* handled */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Record vitals — {appointment?.patientName}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Temp °C</Label><Input value={form.tempC} onChange={(e) => setForm({ ...form, tempC: e.target.value })} /></div>
          <div><Label>Pulse</Label><Input value={form.pulse} onChange={(e) => setForm({ ...form, pulse: e.target.value })} /></div>
          <div><Label>BP Systolic</Label><Input value={form.bpSystolic} onChange={(e) => setForm({ ...form, bpSystolic: e.target.value })} /></div>
          <div><Label>BP Diastolic</Label><Input value={form.bpDiastolic} onChange={(e) => setForm({ ...form, bpDiastolic: e.target.value })} /></div>
          <div><Label>Resp rate</Label><Input value={form.respRate} onChange={(e) => setForm({ ...form, respRate: e.target.value })} /></div>
          <div><Label>SpO₂ %</Label><Input value={form.spO2} onChange={(e) => setForm({ ...form, spO2: e.target.value })} /></div>
          <div><Label>Height cm</Label><Input value={form.heightCm} onChange={(e) => setForm({ ...form, heightCm: e.target.value })} /></div>
          <div><Label>Weight kg</Label><Input value={form.weightKg} onChange={(e) => setForm({ ...form, weightKg: e.target.value })} /></div>
          <div className="col-span-2"><Label>BMI (auto)</Label><Input value={bmi} disabled /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={record.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Save vitals</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
